import { Body, Button, Icon } from '@assembly-js/design-system';
import clsx from 'clsx';
import { useFormikContext } from 'formik';
import { isString } from 'lodash';
import React, { useCallback, useContext } from 'react';
import { useDropzone } from 'react-dropzone-latest';
import { v4 } from 'uuid';

import { notify } from 'src/clients/ApiService';
import { ImageCropper } from 'src/components/ImageCropper';
import { PortalConfigContext } from 'src/context';
import { useProgressUpload } from 'src/hooks/useProgressUpload';
import { useAppDispatch } from 'src/hooks/useStore';
import { ModalWrapper } from 'src/legacy/components/Modals';
import { DropzoneFilesIcon } from 'src/routes/payments/services/assets/icons/DropzoneFilesIcon';
import {
  MAX_IMAGE_DIMENSION,
  MIN_IMAGE_DIMENSION,
} from 'src/routes/payments/services/types';
import { FileWithPreview } from 'src/types/files';
import { FileUtils, S3Utils } from 'src/utils';
import ImageUtils from 'src/utils/FileUtils';
import {
  prepareAttachmentFile,
  UNREADABLE_IMAGE_MESSAGE,
} from 'src/utils/prepareAttachmentFile';
import { UploadResponse } from 'src/utils/S3Utils';

export type CroppedImage = {
  preview: string;
  name: string | undefined;
};

type Props = {
  imageUrl: string;
};

export const ServiceFileDropzone = ({ imageUrl }: Props) => {
  const [croppedImage, setCroppedImage] = React.useState<string>();
  const [chosenImage, setChosenImage] = React.useState<FileWithPreview>();
  const { setFieldValue } = useFormikContext();
  const { setUploadProgress, startUploadFiles } = useProgressUpload();
  const portalConfig = useContext(PortalConfigContext);
  const [isImageCropperOpen, setOpenImageCropperStatus] =
    React.useState<boolean>(false);
  const latestRequestId = React.useRef(0);

  const aspect = 500 / 500;
  const dispatch = useAppDispatch();

  const handleChangeFile = async (file: CroppedImage | null) => {
    if (!file) {
      setFieldValue('imageUrls', []);
      return;
    }

    const uploadImage = async () => {
      startUploadFiles();
      const randomImageId = v4();
      let imageFileUrl = '';

      try {
        const blob = await fetch(file.preview ?? '').then((r) => r.blob());
        const resizedImage = await ImageUtils.CompressImage(
          blob as File,
          512,
          512,
        );

        const res: UploadResponse = await S3Utils.uploadPortalFile(
          resizedImage,
          `${portalConfig.portalHeader}/images/products/${randomImageId}/${file?.name}`,
          (loaded, totalSize) => {
            setUploadProgress({ totalSize, loaded: [loaded] });
          },
        );

        if (res?.Location) {
          imageFileUrl = res.Location;
        }
      } catch {
        imageFileUrl = '';
      }

      setFieldValue('imageUrls', [imageFileUrl]);
    };

    await uploadImage();
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const filesArray = Array.from(acceptedFiles);
      const file = filesArray.at(0);
      if (!file) return;

      // Track the newest pick so a slower, earlier conversion can't overwrite it.
      const requestId = ++latestRequestId.current;
      let preparedFile: File;
      try {
        // Chrome can't decode HEIC/HEIF for the cropper preview
        preparedFile = await prepareAttachmentFile(file);
      } catch {
        if (requestId === latestRequestId.current) {
          notify({
            status: 'error',
            errorMessage: UNREADABLE_IMAGE_MESSAGE,
            dispatch,
          });
        }
        return;
      }
      if (requestId !== latestRequestId.current) return;

      const preview = URL.createObjectURL(preparedFile);
      const _chosenImage = Object.assign(preparedFile, { preview });
      setChosenImage(_chosenImage);
      setOpenImageCropperStatus(true);
    },
    [dispatch],
  );

  const {
    getRootProps,
    getInputProps,
    open: openFilePicker,
  } = useDropzone({
    onDrop,
    noClick: true,
    // Some OS/browser combos report an empty MIME type for HEIC/HEIF files,
    // so the extensions are needed to pass the dropzone's accept check.
    accept: 'image/*,.heic,.heif',
  });

  const handleCropImage = () => {
    const croppedImgObject = {
      preview: croppedImage ?? '',
      name: chosenImage?.name,
    };
    handleChangeFile(croppedImgObject);
    setOpenImageCropperStatus(false);
  };

  const onCropSucceed = () => {
    const image = new Image();
    image.src = chosenImage?.preview ?? '';
    image.onload = async () => {
      const { width, height } = image;

      const isImageSmall =
        width < MIN_IMAGE_DIMENSION || height < MIN_IMAGE_DIMENSION;
      const isImageLarge =
        width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION;

      if (isImageSmall) {
        notify({
          status: 'error',
          errorMessage:
            'Image is too small. Recommended dimensions are 448x448 to 4096x4096',
          dispatch,
        });
      } else if (isImageLarge) {
        notify({
          status: 'error',
          errorMessage:
            'Image is too large. Recommended dimensions are 448x448 to 4096x4096',
          dispatch,
        });
      } else {
        handleCropImage();
      }
    };
  };

  return (
    <>
      <div {...getRootProps()}>
        {!imageUrl.length && (
          <div className="flex flex-col items-center justify-center border border-dashed border-[#ccc] p-6 gap-4 rounded-4">
            <input {...getInputProps()} />
            <DropzoneFilesIcon style={{ fontSize: 60 }} />
            <div className="flex flex-col justify-center items-center gap-3">
              <Button
                label="Choose an image"
                variant="secondary"
                size="sm"
                className="[&>p]:text-sm"
                onClick={openFilePicker}
              />
              <Body className="text-[12px] text-secondary">
                or drop a file here to upload
              </Body>
            </div>
          </div>
        )}
        <ModalWrapper
          noBorder
          title="Crop your image"
          onClose={() => {
            setOpenImageCropperStatus(false);
          }}
          onSuccess={onCropSucceed}
          open={isImageCropperOpen}
        >
          <div className="h-[400px]">
            {isImageCropperOpen && chosenImage && (
              <ImageCropper
                aspect={aspect}
                onCropComplete={async (_, croppedAreaPixels) => {
                  try {
                    if (chosenImage?.preview) {
                      const croppedImg = await FileUtils.getCroppedImg(
                        chosenImage.preview,
                        croppedAreaPixels,
                        0,
                      );
                      const isBlobString = isString(croppedImg);
                      if (isBlobString) {
                        setCroppedImage(croppedImg);
                      }
                    }
                  } catch (e) {
                    console.warn(e);
                  }
                }}
                image={chosenImage}
              />
            )}
          </div>
        </ModalWrapper>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <input {...getInputProps()} />
        {imageUrl && (
          <div
            className="group relative h-[60px] w-[60px] border border-solid border-non-hover-border box-border rounded-4 [&>img]:h-full [&>img]:w-full [&>img]:rounded-4"
            data-testid="image-preview"
          >
            <img
              src={imageUrl}
              alt="product image"
              className={clsx({
                'object-contain': aspect === 0,
              })}
            />
            {/* remove image button  */}
            <button
              type="button"
              className="absolute inset-0 h-full w-full hidden items-center justify-center text-white text-[15px] bg-[rgba(15,15,15,0.6)] rounded-4 border-none group-hover:flex"
              data-attrib="remove-button"
              onClick={() => {
                setCroppedImage('');
                setChosenImage(undefined);
                handleChangeFile(null);
              }}
            >
              <Icon icon="Trash" className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </>
  );
};
